import {
  filter,
  find,
  flatten,
  groupBy,
  isUndefined,
  partial,
  reduce,
  size,
} from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { useDebounce } from "@lkf/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { natural } from "@tootools/sort";
import { InsightPage } from "@radarly/models/insight-pages/model";
import { Project } from "@radarly/models/project";
import { Workspace, WorkspacePreview } from "@radarly/models/workspace";
import {
  InsightPageRepo,
  InsightPageRepositoryUID,
} from "../repositories/InsightPageRepo";

function isNotTheLastPage<T>(currentPage: T[], limit: number) {
  return size(currentPage) >= limit;
}

function getNextPage<T>(allPages: T[][], limit: number) {
  return size(allPages) * limit;
}

function getNextPageParamFromLimit<T>(
  limit: number,
  currentPage: T[],
  allPages: T[][]
) {
  if (isNotTheLastPage(currentPage, limit)) {
    return getNextPage(allPages, limit);
  }
  return undefined;
}

function formatWorkspacePages(
  workspaceList: Workspace[],
  workspaceId: number,
  pages: InsightPage[]
): WorkspacePreview {
  const dashboard = find(workspaceList, { id: workspaceId });
  if (!isUndefined(dashboard)) {
    return { id: workspaceId, label: dashboard.label, pages };
  }
}

function getSearchResults(
  allPages: InsightPage[],
  userWorkspaces: Workspace[]
) {
  const pagesByDashboardId = groupBy(
    filter(allPages, (page) => page.dashboardId !== null),
    "dashboardId"
  );

  const results = reduce(
    pagesByDashboardId,
    (acc, pages, dashId) => {
      const result = formatWorkspacePages(
        userWorkspaces,
        parseInt(dashId), // We grouped by `dashboardId` which is a number
        pages
      );
      if (!isUndefined(result)) {
        acc.push(result);
      }
      return acc;
    },
    [] as WorkspacePreview[]
  );

  // groupBy returns an object (where properties have no order) so we need to sort here
  return natural(results, "label");
}

function getWorkspacePages(
  allPages: InsightPage[],
  userWorkspaces: Workspace[],
  workspaceId: Workspace["id"]
) {
  const result = formatWorkspacePages(userWorkspaces, workspaceId, allPages);
  return !isUndefined(result) ? [result] : [];
}

export function useSearchInsightPages(
  project: Project,
  userWorkspaces: Workspace[],
  search: string,
  workspaceId: number,
  sort: string,
  limit: number = 20
) {
  const repository = useInjection<InsightPageRepo>(InsightPageRepositoryUID);
  const debouncedSearch = useDebounce(search, 500);
  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteQuery(
      ["/insight-pages/search", debouncedSearch, workspaceId, sort],
      ({ pageParam = 0 }) => {
        return repository.searchPages(
          project.id,
          debouncedSearch,
          workspaceId,
          { start: pageParam, limit },
          sort
        );
      },
      {
        getNextPageParam: partial(getNextPageParamFromLimit, limit),
      }
    );
  const allPages = flatten(data?.pages);
  const workspacesWithPages = workspaceId
    ? getWorkspacePages(allPages, userWorkspaces, workspaceId)
    : getSearchResults(allPages, userWorkspaces);
  const globalPages = {
    pages: filter(allPages, { dashboardId: null }),
  };
  return {
    pages: {
      workspaces: workspacesWithPages,
      globalPages,
    },
    loading: !!isLoading,
    isFetchingNextPage,
    fetchMore: fetchNextPage,
    canFetchMore: hasNextPage,
  };
}
